import type { AppLocale } from '../messages';

/**
 * Строки участка «music» — личный плеер. Свой файл у каждого участка, а не общий `messages.ts`:
 * участки правятся параллельно, и общий файл давал бы конфликт на каждой новой строке.
 * Ключ добавляется во все четыре словаря — `en` задаёт набор, остальные проверяются типом.
 */
const en = {
  musicOpenA11y: 'Music player',
  musicTitle: 'Music',
  musicClose: 'Close player',
  musicEmptyTitle: 'Your music, right on the phone',
  musicEmptyBody:
    'Add tracks from the device files. They stay on this device: nothing is uploaded and no traffic is used. MP3, FLAC, ALAC, AAC, Ogg, Opus and WAV play as they are, without re-encoding.',
  musicAdd: 'Add music',
  musicImporting: 'Adding {done} of {total}…',
  musicImportAdded: 'Added tracks: {n}',
  musicImportDuplicates: 'Already in the library: {n}',
  musicImportFailed: 'Could not read: {n}',
  musicImportNothing: 'No new tracks',
  musicTracksCount: 'Tracks: {n}',
  musicUnknownArtist: 'Unknown artist',
  musicPlay: 'Play',
  musicPause: 'Pause',
  musicNext: 'Next track',
  musicPrevious: 'Previous track',
  musicShuffleOn: 'Shuffle is on',
  musicShuffleOff: 'Shuffle is off',
  musicRepeatOff: 'Repeat is off',
  musicRepeatAll: 'Repeat all',
  musicRepeatOne: 'Repeat one track',
  musicSeekA11y: 'Playback position',
  musicRemoveConfirmTitle: 'Remove the track?',
  musicRemoveConfirmBody: '“{title}” will be deleted from this device but stays in your playlists and on your other devices. “Delete everywhere” also removes it from playlists. The original file in your files stays where it was.',
  musicLossless: 'Lossless',
  musicUnitBit: 'bit',
  musicUnitKhz: 'kHz',
  musicUpdateApp: 'Update the app to listen to music: this version has no audio engine yet.',
  musicPausedForCall: 'Music is paused while you are on a call',
  musicEqualizer: 'Equalizer',
  musicEqOn: 'Equalizer is on',
  musicEqOff: 'Equalizer is off',
  musicEqPreamp: 'Volume lowered by {db} dB so that boosted bands do not distort',
  musicEqTransparent: 'Sound goes straight from the file — no processing',
  musicNormalize: 'Even out loudness',
  musicNormalizeHint: 'Quiet and loud tracks play at the same level when the file has ReplayGain tags',
  musicPreset_flat: 'Flat',
  musicPreset_bass: 'Bass',
  musicPreset_treble: 'Treble',
  musicPreset_vocal: 'Vocal',
  musicPreset_rock: 'Rock',
  musicPreset_pop: 'Pop',
  musicPreset_jazz: 'Jazz',
  musicPreset_classical: 'Classical',
  musicPreset_electronic: 'Electronic',
  musicPreset_night: 'Night',
  musicPreset_custom: 'Custom',
  musicAllMusic: 'All music',
  musicNewPlaylist: 'New playlist',
  musicPlaylistDefaultName: 'Playlist',
  musicPlaylistNamePlaceholder: 'Playlist name',
  musicDone: 'Done',
  musicAddToPlaylist: 'Add to playlist',
  musicRemoveFromPlaylist: 'Remove from “{name}”',
  musicDeleteFromDevice: 'Delete from device',
  musicDeleteEverywhere: 'Delete everywhere',
  musicPlaybackFailed: 'Can’t play “{title}”',
  musicBlockedByCall: 'Music doesn’t play during a call',
  musicSaveFailed: 'Couldn’t save the music library',
  musicRetry: 'Retry',
  musicAddedToPlaylist: 'Added to “{name}”',
  musicRemovedFromPlaylist: 'Removed from “{name}”',
  musicRenamePlaylist: 'Rename playlist',
  musicDeletePlaylist: 'Delete playlist',
  musicDeletePlaylistBody: '“{name}” will be deleted. The tracks stay in “All music”.',
  musicPlaylistEmpty: 'The playlist is empty. Open “All music” and add tracks with the ⋯ button next to a track.',
  musicPlayAll: 'Play',
  musicShufflePlay: 'Shuffle',
  musicMoreA11y: 'Actions for “{title}”',
  musicMissing: 'Not on this device',
  musicRestoreTitle: 'Your playlists are back',
  musicRestoreBody: 'Found playlists: {playlists}, tracks: {tracks} from your previous install. Add these files from the device and they will return to their places on their own.',
  musicRestoreMore: 'and {n} more',
  musicMissingBanner:
    'Not on this device: {n} — added on another device or before a reinstall. Music files are not synced, only playlists: add the same files here and they take their places on their own.',
  musicRestoredToast: 'Back in playlists: {n}',
  musicForgetTrack: 'Forget this track',
  musicForgetTrackBody: 'The file is not on this device. The track will be removed from all playlists.',
  musicPlaylistActionsA11y: 'Playlist actions',
  musicPreset_punk: 'Punk hardcore',
  musicPreset_rave: 'Rave',
  musicArtists: 'Artists',
  musicArtistsCount: 'Artists: {n}',
  musicArtistCounts: 'Albums: {albums} · tracks: {tracks}',
  musicNoAlbum: 'No album',
  musicBack: 'Back',
  musicEditInfo: 'Edit details',
  musicEditArtist: 'Rename artist',
  musicEditAlbum: 'Edit album',
  musicNameArtist: 'Name the artist',
  musicFieldTitle: 'Title',
  musicFieldArtist: 'Artist',
  musicFieldAlbum: 'Album',
  musicInfoSaved: 'Saved',
  musicUnknownArtistBanner: 'The files do not say who this is. Tap to enter the artist and album — they will show up on your other devices too.',
} as const satisfies Record<string, string>;

export type MusicMessageKey = keyof typeof en;

export const musicMessages: Record<AppLocale, Record<MusicMessageKey, string>> = {
  en,
  ru: {
    musicOpenA11y: 'Музыкальный плеер',
    musicTitle: 'Музыка',
    musicClose: 'Закрыть плеер',
    musicEmptyTitle: 'Ваша музыка — прямо в телефоне',
    musicEmptyBody:
      'Добавьте треки из файлов устройства. Они остаются на этом устройстве: ничего не загружается на сервер и не тратит трафик. MP3, FLAC, ALAC, AAC, Ogg, Opus и WAV играют как есть, без перекодирования.',
    musicAdd: 'Добавить музыку',
    musicImporting: 'Добавляем {done} из {total}…',
    musicImportAdded: 'Добавлено треков: {n}',
    musicImportDuplicates: 'Уже были в фонотеке: {n}',
    musicImportFailed: 'Не удалось прочитать: {n}',
    musicImportNothing: 'Новых треков нет',
    musicTracksCount: 'Треков: {n}',
    musicUnknownArtist: 'Неизвестный исполнитель',
    musicPlay: 'Играть',
    musicPause: 'Пауза',
    musicNext: 'Следующий трек',
    musicPrevious: 'Предыдущий трек',
    musicShuffleOn: 'Перемешивание включено',
    musicShuffleOff: 'Перемешивание выключено',
    musicRepeatOff: 'Повтор выключен',
    musicRepeatAll: 'Повторять все',
    musicRepeatOne: 'Повторять один трек',
    musicSeekA11y: 'Позиция воспроизведения',
    musicRemoveConfirmTitle: 'Убрать трек?',
    musicRemoveConfirmBody: '«{title}» будет удалён с этого устройства, но останется в плейлистах и на других устройствах. «Удалить везде» уберёт его и из плейлистов. Исходный файл в «Файлах» останется на месте.',
    musicLossless: 'Без потерь',
    musicUnitBit: 'бит',
    musicUnitKhz: 'кГц',
    musicUpdateApp: 'Чтобы слушать музыку, обновите приложение: в этой версии ещё нет звукового движка.',
    musicPausedForCall: 'Музыка на паузе, пока идёт звонок',
    musicEqualizer: 'Эквалайзер',
    musicEqOn: 'Эквалайзер включён',
    musicEqOff: 'Эквалайзер выключен',
    musicEqPreamp: 'Громкость снижена на {db} дБ, чтобы поднятые полосы не хрипели',
    musicEqTransparent: 'Звук идёт прямо из файла — без обработки',
    musicNormalize: 'Выравнивать громкость',
    musicNormalizeHint: 'Тихие и громкие треки звучат на одном уровне, если в файле есть теги ReplayGain',
    musicPreset_flat: 'Ровно',
    musicPreset_bass: 'Бас',
    musicPreset_treble: 'Верха',
    musicPreset_vocal: 'Голос',
    musicPreset_rock: 'Рок',
    musicPreset_pop: 'Поп',
    musicPreset_jazz: 'Джаз',
    musicPreset_classical: 'Классика',
    musicPreset_electronic: 'Электроника',
    musicPreset_night: 'Ночью',
    musicPreset_custom: 'Свой',
    musicAllMusic: 'Вся музыка',
    musicNewPlaylist: 'Новый плейлист',
    musicPlaylistDefaultName: 'Плейлист',
    musicPlaylistNamePlaceholder: 'Название плейлиста',
    musicDone: 'Готово',
    musicAddToPlaylist: 'Добавить в плейлист',
    musicRemoveFromPlaylist: 'Убрать из «{name}»',
    musicDeleteFromDevice: 'Удалить с устройства',
    musicDeleteEverywhere: 'Удалить везде',
    musicPlaybackFailed: 'Не удалось воспроизвести «{title}»',
    musicBlockedByCall: 'Во время звонка музыка не играет',
    musicSaveFailed: 'Не удалось сохранить фонотеку',
    musicRetry: 'Повторить',
    musicAddedToPlaylist: 'Добавлено в «{name}»',
    musicRemovedFromPlaylist: 'Убрано из «{name}»',
    musicRenamePlaylist: 'Переименовать плейлист',
    musicDeletePlaylist: 'Удалить плейлист',
    musicDeletePlaylistBody: '«{name}» будет удалён. Треки останутся во «Всей музыке».',
    musicPlaylistEmpty: 'Плейлист пуст. Откройте «Вся музыка» и добавьте треки кнопкой ⋯ рядом с треком.',
    musicPlayAll: 'Слушать',
    musicShufflePlay: 'Перемешать',
    musicMoreA11y: 'Действия с «{title}»',
    musicMissing: 'Нет на устройстве',
    musicRestoreTitle: 'Ваши плейлисты на месте',
    musicRestoreBody: 'Нашли с прошлой установки плейлистов: {playlists}, треков: {tracks}. Добавьте эти файлы с устройства — они сами встанут на свои места.',
    musicRestoreMore: 'и ещё {n}',
    musicMissingBanner:
      'Нет на этом устройстве: {n} — добавлены на другом устройстве или до переустановки. Сами файлы не синхронизируются, только плейлисты: добавьте эти же файлы сюда — и они встанут на свои места.',
    musicRestoredToast: 'Вернулось в плейлисты: {n}',
    musicForgetTrack: 'Забыть трек',
    musicForgetTrackBody: 'Файла нет на этом устройстве. Трек будет убран из всех плейлистов.',
    musicPlaylistActionsA11y: 'Действия с плейлистом',
    musicPreset_punk: 'Панк-хардкор',
    musicPreset_rave: 'Рейв',
    musicArtists: 'Исполнители',
    musicArtistsCount: 'Исполнителей: {n}',
    musicArtistCounts: 'Альбомов: {albums} · треков: {tracks}',
    musicNoAlbum: 'Без альбома',
    musicBack: 'Назад',
    musicEditInfo: 'Изменить сведения',
    musicEditArtist: 'Переименовать исполнителя',
    musicEditAlbum: 'Изменить альбом',
    musicNameArtist: 'Указать исполнителя',
    musicFieldTitle: 'Название',
    musicFieldArtist: 'Исполнитель',
    musicFieldAlbum: 'Альбом',
    musicInfoSaved: 'Сохранено',
    musicUnknownArtistBanner: 'В файлах не указано, чьи это песни. Нажмите, чтобы вписать исполнителя и альбом, — они появятся и на других устройствах.',
  },
  sr: {
    musicOpenA11y: 'Muzički plejer',
    musicTitle: 'Muzika',
    musicClose: 'Zatvori plejer',
    musicEmptyTitle: 'Vaša muzika — pravo u telefonu',
    musicEmptyBody:
      'Dodajte pesme iz datoteka uređaja. Ostaju na ovom uređaju: ništa se ne šalje na server i ne troši se saobraćaj. MP3, FLAC, ALAC, AAC, Ogg, Opus i WAV puštaju se kakvi jesu, bez ponovnog kodiranja.',
    musicAdd: 'Dodaj muziku',
    musicImporting: 'Dodajemo {done} od {total}…',
    musicImportAdded: 'Dodato pesama: {n}',
    musicImportDuplicates: 'Već u fonoteci: {n}',
    musicImportFailed: 'Nije moguće pročitati: {n}',
    musicImportNothing: 'Nema novih pesama',
    musicTracksCount: 'Pesama: {n}',
    musicUnknownArtist: 'Nepoznat izvođač',
    musicPlay: 'Pusti',
    musicPause: 'Pauza',
    musicNext: 'Sledeća pesma',
    musicPrevious: 'Prethodna pesma',
    musicShuffleOn: 'Nasumično je uključeno',
    musicShuffleOff: 'Nasumično je isključeno',
    musicRepeatOff: 'Ponavljanje je isključeno',
    musicRepeatAll: 'Ponavljaj sve',
    musicRepeatOne: 'Ponavljaj jednu pesmu',
    musicSeekA11y: 'Pozicija reprodukcije',
    musicRemoveConfirmTitle: 'Ukloniti pesmu?',
    musicRemoveConfirmBody: '„{title}” biće obrisana sa ovog uređaja, ali ostaje u plejlistama i na drugim uređajima. „Obriši svuda” je uklanja i iz plejlista. Originalna datoteka ostaje gde je bila.',
    musicLossless: 'Bez gubitaka',
    musicUnitBit: 'bit',
    musicUnitKhz: 'kHz',
    musicUpdateApp: 'Ažurirajte aplikaciju da biste slušali muziku: ova verzija još nema zvučni mehanizam.',
    musicPausedForCall: 'Muzika je pauzirana dok traje poziv',
    musicEqualizer: 'Ekvilajzer',
    musicEqOn: 'Ekvilajzer je uključen',
    musicEqOff: 'Ekvilajzer je isključen',
    musicEqPreamp: 'Jačina smanjena za {db} dB da pojačani opsezi ne bi krčali',
    musicEqTransparent: 'Zvuk ide pravo iz datoteke — bez obrade',
    musicNormalize: 'Ujednači jačinu',
    musicNormalizeHint: 'Tihe i glasne pesme zvuče isto glasno ako datoteka ima ReplayGain oznake',
    musicPreset_flat: 'Ravno',
    musicPreset_bass: 'Bas',
    musicPreset_treble: 'Visoki',
    musicPreset_vocal: 'Vokal',
    musicPreset_rock: 'Rok',
    musicPreset_pop: 'Pop',
    musicPreset_jazz: 'Džez',
    musicPreset_classical: 'Klasika',
    musicPreset_electronic: 'Elektronika',
    musicPreset_night: 'Noću',
    musicPreset_custom: 'Svoje',
    musicAllMusic: 'Sva muzika',
    musicNewPlaylist: 'Nova plejlista',
    musicPlaylistDefaultName: 'Plejlista',
    musicPlaylistNamePlaceholder: 'Naziv plejliste',
    musicDone: 'Gotovo',
    musicAddToPlaylist: 'Dodaj u plejlistu',
    musicRemoveFromPlaylist: 'Ukloni iz „{name}”',
    musicDeleteFromDevice: 'Obriši sa uređaja',
    musicDeleteEverywhere: 'Obriši svuda',
    musicPlaybackFailed: 'Ne može da se pusti „{title}”',
    musicBlockedByCall: 'Muzika ne svira tokom poziva',
    musicSaveFailed: 'Muzička biblioteka nije sačuvana',
    musicRetry: 'Pokušaj ponovo',
    musicAddedToPlaylist: 'Dodato u „{name}”',
    musicRemovedFromPlaylist: 'Uklonjeno iz „{name}”',
    musicRenamePlaylist: 'Preimenuj plejlistu',
    musicDeletePlaylist: 'Obriši plejlistu',
    musicDeletePlaylistBody: '„{name}” biće obrisana. Pesme ostaju u „Sva muzika”.',
    musicPlaylistEmpty: 'Plejlista je prazna. Otvorite „Sva muzika” i dodajte pesme dugmetom ⋯ pored pesme.',
    musicPlayAll: 'Slušaj',
    musicShufflePlay: 'Nasumično',
    musicMoreA11y: 'Radnje za „{title}”',
    musicMissing: 'Nije na uređaju',
    musicRestoreTitle: 'Vaše plejliste su na mestu',
    musicRestoreBody: 'Iz prethodne instalacije pronađeno plejlista: {playlists}, pesama: {tracks}. Dodajte ove datoteke sa uređaja i same će se vratiti na svoja mesta.',
    musicRestoreMore: 'i još {n}',
    musicMissingBanner:
      'Nema na ovom uređaju: {n} — dodate na drugom uređaju ili pre ponovne instalacije. Same datoteke se ne sinhronizuju, samo plejliste: dodajte iste datoteke ovde i same će stati na svoja mesta.',
    musicRestoredToast: 'Vraćeno u plejliste: {n}',
    musicForgetTrack: 'Zaboravi pesmu',
    musicForgetTrackBody: 'Datoteke nema na ovom uređaju. Pesma će biti uklonjena iz svih plejlista.',
    musicPlaylistActionsA11y: 'Radnje s plejlistom',
    musicPreset_punk: 'Pank-hardkor',
    musicPreset_rave: 'Rejv',
    musicArtists: 'Izvođači',
    musicArtistsCount: 'Izvođača: {n}',
    musicArtistCounts: 'Albuma: {albums} · numera: {tracks}',
    musicNoAlbum: 'Bez albuma',
    musicBack: 'Nazad',
    musicEditInfo: 'Izmeni podatke',
    musicEditArtist: 'Preimenuj izvođača',
    musicEditAlbum: 'Izmeni album',
    musicNameArtist: 'Unesi izvođača',
    musicFieldTitle: 'Naziv',
    musicFieldArtist: 'Izvođač',
    musicFieldAlbum: 'Album',
    musicInfoSaved: 'Sačuvano',
    musicUnknownArtistBanner: 'U datotekama ne piše čije su ovo pesme. Dodirnite da unesete izvođača i album — pojaviće se i na drugim uređajima.',
  },
  fr: {
    musicOpenA11y: 'Lecteur de musique',
    musicTitle: 'Musique',
    musicClose: 'Fermer le lecteur',
    musicEmptyTitle: 'Votre musique, directement sur le téléphone',
    musicEmptyBody:
      'Ajoutez des morceaux depuis les fichiers de l’appareil. Ils restent sur cet appareil : rien n’est envoyé au serveur, aucune donnée mobile n’est consommée. MP3, FLAC, ALAC, AAC, Ogg, Opus et WAV sont lus tels quels, sans réencodage.',
    musicAdd: 'Ajouter de la musique',
    musicImporting: 'Ajout de {done} sur {total}…',
    musicImportAdded: 'Morceaux ajoutés : {n}',
    musicImportDuplicates: 'Déjà dans la bibliothèque : {n}',
    musicImportFailed: 'Illisibles : {n}',
    musicImportNothing: 'Aucun nouveau morceau',
    musicTracksCount: 'Morceaux : {n}',
    musicUnknownArtist: 'Artiste inconnu',
    musicPlay: 'Lecture',
    musicPause: 'Pause',
    musicNext: 'Morceau suivant',
    musicPrevious: 'Morceau précédent',
    musicShuffleOn: 'Lecture aléatoire activée',
    musicShuffleOff: 'Lecture aléatoire désactivée',
    musicRepeatOff: 'Répétition désactivée',
    musicRepeatAll: 'Tout répéter',
    musicRepeatOne: 'Répéter un morceau',
    musicSeekA11y: 'Position de lecture',
    musicRemoveConfirmTitle: 'Retirer le morceau ?',
    musicRemoveConfirmBody: '« {title} » sera supprimé de cet appareil, mais restera dans vos playlists et sur vos autres appareils. « Supprimer partout » le retire aussi des playlists. Le fichier d’origine reste à sa place.',
    musicLossless: 'Sans perte',
    musicUnitBit: 'bits',
    musicUnitKhz: 'kHz',
    musicUpdateApp: 'Mettez l’application à jour pour écouter de la musique : cette version n’a pas encore de moteur audio.',
    musicPausedForCall: 'La musique est en pause pendant l’appel',
    musicEqualizer: 'Égaliseur',
    musicEqOn: 'Égaliseur activé',
    musicEqOff: 'Égaliseur désactivé',
    musicEqPreamp: 'Volume réduit de {db} dB pour que les bandes amplifiées ne saturent pas',
    musicEqTransparent: 'Le son sort directement du fichier — sans traitement',
    musicNormalize: 'Égaliser le volume',
    musicNormalizeHint: 'Les morceaux calmes et forts sont joués au même niveau si le fichier a des balises ReplayGain',
    musicPreset_flat: 'Neutre',
    musicPreset_bass: 'Basses',
    musicPreset_treble: 'Aigus',
    musicPreset_vocal: 'Voix',
    musicPreset_rock: 'Rock',
    musicPreset_pop: 'Pop',
    musicPreset_jazz: 'Jazz',
    musicPreset_classical: 'Classique',
    musicPreset_electronic: 'Électro',
    musicPreset_night: 'Nuit',
    musicPreset_custom: 'Perso',
    musicAllMusic: 'Toute la musique',
    musicNewPlaylist: 'Nouvelle playlist',
    musicPlaylistDefaultName: 'Playlist',
    musicPlaylistNamePlaceholder: 'Nom de la playlist',
    musicDone: 'Terminé',
    musicAddToPlaylist: 'Ajouter à une playlist',
    musicRemoveFromPlaylist: 'Retirer de « {name} »',
    musicDeleteFromDevice: 'Supprimer de l’appareil',
    musicDeleteEverywhere: 'Supprimer partout',
    musicPlaybackFailed: 'Impossible de lire « {title} »',
    musicBlockedByCall: 'La musique ne joue pas pendant un appel',
    musicSaveFailed: 'Impossible d’enregistrer la bibliothèque musicale',
    musicRetry: 'Réessayer',
    musicAddedToPlaylist: 'Ajouté à « {name} »',
    musicRemovedFromPlaylist: 'Retiré de « {name} »',
    musicRenamePlaylist: 'Renommer la playlist',
    musicDeletePlaylist: 'Supprimer la playlist',
    musicDeletePlaylistBody: '« {name} » sera supprimée. Les morceaux restent dans « Toute la musique ».',
    musicPlaylistEmpty: 'La playlist est vide. Ouvrez « Toute la musique » et ajoutez des morceaux avec le bouton ⋯ à côté d’un morceau.',
    musicPlayAll: 'Écouter',
    musicShufflePlay: 'Aléatoire',
    musicMoreA11y: 'Actions pour « {title} »',
    musicMissing: 'Absent de l’appareil',
    musicRestoreTitle: 'Vos playlists sont de retour',
    musicRestoreBody: 'Retrouvés de l’installation précédente : playlists {playlists}, morceaux {tracks}. Ajoutez ces fichiers depuis l’appareil et ils reprendront leur place tout seuls.',
    musicRestoreMore: 'et {n} de plus',
    musicMissingBanner:
      'Absents de cet appareil : {n} — ajoutés sur un autre appareil ou avant une réinstallation. Les fichiers ne sont pas synchronisés, seulement les playlists : ajoutez ici les mêmes fichiers et ils reprendront leur place.',
    musicRestoredToast: 'Revenus dans les playlists : {n}',
    musicForgetTrack: 'Oublier ce morceau',
    musicForgetTrackBody: 'Le fichier n’est pas sur cet appareil. Le morceau sera retiré de toutes les playlists.',
    musicPlaylistActionsA11y: 'Actions de la playlist',
    musicPreset_punk: 'Punk hardcore',
    musicPreset_rave: 'Rave',
    musicArtists: 'Artistes',
    musicArtistsCount: 'Artistes : {n}',
    musicArtistCounts: 'Albums : {albums} · titres : {tracks}',
    musicNoAlbum: 'Sans album',
    musicBack: 'Retour',
    musicEditInfo: 'Modifier les infos',
    musicEditArtist: 'Renommer l’artiste',
    musicEditAlbum: 'Modifier l’album',
    musicNameArtist: 'Indiquer l’artiste',
    musicFieldTitle: 'Titre',
    musicFieldArtist: 'Artiste',
    musicFieldAlbum: 'Album',
    musicInfoSaved: 'Enregistré',
    musicUnknownArtistBanner: 'Les fichiers n’indiquent pas l’artiste. Touchez pour saisir l’artiste et l’album — ils apparaîtront aussi sur vos autres appareils.',
  },
};
